import { AbstractMesh, Color3, Mesh, Vector3 } from '@babylonjs/core';
import { Builder, IBuilder } from 'builder-pattern';
import { AssetObject } from './AssetObject';
import { CollectibleAttractionBehavior } from './behavior/CollectibleAttractionBehavior';
import { Entity } from './Entity';
import { Level } from './Level';
import { AssetObjectConfig, MeshConfig, MeshFactory } from './MeshFactory';
import { Player } from './Player';
import { PlayerCollision } from './PlayerCollision';

export interface Collectible extends Entity {
  id: string;
  collected: boolean;
}

export enum CollectibleType {
  DIAMOND = 'diamond', // player.score => plus 1 point
  COIN = 'coin', // player.coins => 100 coins = 1 life
  TIME = 'time', // level.time => 10 seconds more time
  LIFE = 'life', // player.lives => plus 1 life
  AMMO = 'ammo', // player.ammunition => plus 1 ammo
  AIRJUMP = 'airjump', // player.airJumps => plus 1 air jump
  SHIELD = 'shield', // player invulnerability for x seconds
}

export class CollectibleConfig {
  static builder(): IBuilder<CollectibleConfig> {
    return Builder(CollectibleConfig, {});
  }
  id: string;
  type: CollectibleType;
  position: Vector3;
}

export class CollectibleFactory {
  static highlightColor: Color3 = Color3.Yellow();
  static meshesByType: Map<string, Mesh> = new Map<string, Mesh>();
  static index: number = 0;

  private static readonly COLLECTIBLE_UI_DATA = new Map<CollectibleType, { icon: string; target: string }>([
    [CollectibleType.DIAMOND, { icon: '💎', target: 'score' }],
    [CollectibleType.COIN, { icon: '💰', target: 'coins' }],
    [CollectibleType.TIME, { icon: '⏱️', target: 'time' }],
    [CollectibleType.LIFE, { icon: '❤️', target: 'lives' }],
    [CollectibleType.AMMO, { icon: '🍎', target: 'ammunition' }],
    [CollectibleType.AIRJUMP, { icon: '💨', target: 'airJumps' }],
  ]);

  public static create(config: CollectibleConfig, level: Level, pathIndex: number): Collectible {
    let sound = 'collect';
    let meshConfig: MeshConfig = undefined;

    // Callback to update stats
    let updateStats: (player: Player) => void = () => {};
    let message: string | undefined = undefined;

    switch (config.type) {
      case CollectibleType.DIAMOND: {
        sound = 'collectGem';
        updateStats = (player) => {
          player.score += 10;
        };
        meshConfig = AssetObjectConfig.builder()
          .object(new AssetObject('jewel', 'jewel').withMaterialProperties(1, 0.5, new Color3(2, 2, 2)).withCenterOrigin(true))
          .name('diamond')
          .physicsShapeType(undefined)
          .scale(2)
          .highlight(CollectibleFactory.highlightColor)
          .build();
        break;
      }
      case CollectibleType.COIN: {
        sound = 'collectCoin';
        updateStats = (player) => {
          player.coins += 1;
          if (player.coins % 100 === 0) {
            player.lives += 1;
            player.coins = 0;
            level.hud.showMessage('Life Added!', 1000, true);
          }
        };
        meshConfig = AssetObjectConfig.builder()
          .object(new AssetObject('coin', 'coin-gold').withMaterialProperties(1, 0.5, new Color3(2, 2, 2)).withCenterOrigin(true))
          .name('coin')
          .physicsShapeType(undefined)
          .scale(2)
          .highlight(CollectibleFactory.highlightColor)
          .build();
        break;
      }
      case CollectibleType.LIFE: {
        sound = 'collectHeart';
        updateStats = (player) => {
          player.lives += 1;
        };
        meshConfig = AssetObjectConfig.builder()
          .object(new AssetObject('heart', 'heart').withMaterialProperties(1, 0.5, new Color3(2, 2, 2)).withCenterOrigin(true))
          .name('life')
          .physicsShapeType(undefined)
          .scale(2)
          .highlight(CollectibleFactory.highlightColor)
          .build();
        break;
      }
      case CollectibleType.AMMO: {
        updateStats = (player) => {
          player.ammunition += 1;
        };
        meshConfig = AssetObjectConfig.builder()
          .object(new AssetObject('projectile', 'apple').withMaterialProperties(1, 0.5, new Color3(2, 2, 2)).withCenterOrigin(true))
          .name('ammo')
          .physicsShapeType(undefined)
          .scale(4)
          .highlight(CollectibleFactory.highlightColor)
          .build();
        break;
      }
      case CollectibleType.TIME: {
        updateStats = (player) => {
          level.timeLimit += 10;
        };
        message = 'Time Extended!';
        meshConfig = AssetObjectConfig.builder()
          .object(new AssetObject('prize-wheel').withMaterialProperties(1, 0.5, new Color3(2, 2, 2)).withCenterOrigin(true))
          .name('time')
          .physicsShapeType(undefined)
          .scale(1)
          .highlight(CollectibleFactory.highlightColor)
          .build();
        break;
      }
      case CollectibleType.AIRJUMP: {
        updateStats = (player) => {
          player.maxAirJumps += 1;
        };
        message = 'Air Jump Unlocked!';
        meshConfig = AssetObjectConfig.builder()
          .object(new AssetObject('crate', 'crate-item-strong').withMaterialProperties(1, 0.5, new Color3(2, 2, 2)).withCenterOrigin(true))
          .name('airjump')
          .physicsShapeType(undefined)
          .scale(1)
          .highlight(CollectibleFactory.highlightColor)
          .build();
        break;
      }
      case CollectibleType.SHIELD: {
        updateStats = (player) => {
          player.vulnerable = false;
          player.startShield();
        };
        message = 'Shield collected!';
        meshConfig = AssetObjectConfig.builder()
          .object(new AssetObject('shield', 'weapon-shield').withMaterialProperties(1, 0.5, new Color3(2, 2, 2)).withCenterOrigin(true))
          .name('shield')
          .physicsShapeType(undefined)
          .rotation(new Vector3(-Math.PI / 2, 0, 0))
          .scale(2)
          .highlight(CollectibleFactory.highlightColor)
          .build();
        break;
      }
    }

    if (!CollectibleFactory.meshesByType.has(config.type)) {
      // Set clone=true to ensure buildMesh applies the transform reset logic
      (meshConfig as AssetObjectConfig).object.clone = true;
      const originalMesh = meshConfig.buildMesh(level);

      // Configure the source mesh (applies scale, behaviors, highlight), before the mesh is used as a source for instances
      MeshFactory.configureMesh(originalMesh, meshConfig, level);

      // Mark as source mesh
      originalMesh.name += '_original';
      originalMesh.isVisible = false;
      originalMesh.setEnabled(false);

      CollectibleFactory.meshesByType.set(config.type, originalMesh);
    }

    const original = CollectibleFactory.meshesByType.get(config.type);

    const instance = original.createInstance(original.name + '_inst_' + CollectibleFactory.index++);
    instance.isVisible = true;
    instance.setEnabled(true);

    // Add attraction behavior
    instance.addBehavior(new CollectibleAttractionBehavior().initLevel(level));

    // Clear rotationQuaternion so Euler rotation (rotation.y) works in update loop
    instance.rotationQuaternion = null;

    // Position the instance
    instance.position = config.position.clone();

    // Copy metadata from source for collision detection
    instance.metadata = { ...original.metadata };

    const collectible: Collectible = {
      id: config.id,
      mesh: instance,
      config,
      pathIndex,
      collected: false,
    };

    // Define collision handler logic
    const handleCollision = (player: Player, mesh: AbstractMesh) => {
      if (collectible.collected) return;
      collectible.collected = true;

      // Broadcast pickup to network
      if (level.game.network && level.game.network.isConnected()) {
        level.game.network.sendMessage('COLLECTIBLE_PICKUP', { id: config.id });
      }

      // Immediate visual feedback
      mesh.isVisible = false;
      level.game.sound.play(sound);
      level.particles.createDisappearanceParticleSystem(mesh, 0.4);

      // Animation & Update
      const uiData = CollectibleFactory.COLLECTIBLE_UI_DATA.get(config.type);
      if (uiData) {
        level.hud.animateCollection(mesh.getAbsolutePosition(), uiData.icon, uiData.target, () => {
          updateStats(player);
          player.collectibles++;
          if (message) {
            level.hud.showMessage(message, 1000, true);
          }
          // Defer dispose to next frame to avoid a GPU stall mid-render
          setTimeout(() => mesh.dispose(), 0);
        });
      } else {
        mesh.isVisible = false;
        updateStats(player);
        player.collectibles++;
        if (message) {
          level.hud.showMessage(message, 1000, true);
        }
        // Defer dispose to next frame to avoid a GPU stall mid-render
        setTimeout(() => mesh.dispose(), 0);
      }
    };

    // Attach collision handler
    // meshConfig.onPlayerCollision = handleCollision; // Not needed for instance

    // Assign collision handler to instance metadata manually since specific closure is needed
    instance.metadata.playerCollision = new PlayerCollision(true, (level: Level, player: Player, event: any) =>
      handleCollision(player, instance)
    );

    return collectible;
  }

  static dispose() {
    for (const mesh of CollectibleFactory.meshesByType.values()) {
      mesh.dispose();
    }
    CollectibleFactory.meshesByType.clear();
  }
}
